import {
  MediaProcessingStatus,
  UserFileAttentionFilter,
  VirusScanStatus
} from '@roadfootage/api-client'
import type { UserFile, UserFileStatusCountsResponse } from '@roadfootage/api-client'

/** The staff Files quick filters (RF-131). `null` is "All". The chips and the `?attention=` URL share these values. */
export type FileAttention = UserFileAttentionFilter | null

export interface FileAttentionChip {
  value: FileAttention
  label: string
  count: (counts: UserFileStatusCountsResponse) => number
}

export const FILE_ATTENTION_CHIPS: FileAttentionChip[] = [
  { value: null, label: 'All', count: (c) => c.all ?? 0 },
  {
    value: UserFileAttentionFilter.NeedsAttention,
    label: 'Needs attention',
    count: (c) => c.needsAttention ?? 0
  },
  {
    value: UserFileAttentionFilter.Failed,
    label: 'Failed processing',
    count: (c) => c.failed ?? 0
  },
  { value: UserFileAttentionFilter.Threat, label: 'Threat detected', count: (c) => c.threat ?? 0 },
  {
    value: UserFileAttentionFilter.Processing,
    label: 'Processing',
    count: (c) => c.processing ?? 0
  },
  { value: UserFileAttentionFilter.Queued, label: 'Queued', count: (c) => c.queued ?? 0 },
  {
    value: UserFileAttentionFilter.PendingScan,
    label: 'Pending scan',
    count: (c) => c.pendingScan ?? 0
  }
]

/** Reads `?attention=` (the first value). Anything that is not a known filter means "All". */
export function parseFileAttention(query: Record<string, unknown>): FileAttention {
  const raw = Array.isArray(query.attention) ? query.attention[0] : query.attention
  const known = Object.values(UserFileAttentionFilter) as string[]
  return typeof raw === 'string' && known.includes(raw) ? (raw as UserFileAttentionFilter) : null
}

export type FileStatusKind = 'threat' | 'failed' | 'processing' | 'pending-scan' | 'ok'

export interface FileStatus {
  kind: FileStatusKind
  label: string
}

/** What a Files row says about the file. A threat outranks a failure, because it decides how staff may handle the file. */
export function fileStatus(
  file: Pick<UserFile, 'virusScanStatus' | 'processingStatus' | 'isUploaded'>
): FileStatus {
  if (file.virusScanStatus === VirusScanStatus.Threat)
    return { kind: 'threat', label: 'Threat detected' }
  if (file.processingStatus === MediaProcessingStatus.Failed)
    return { kind: 'failed', label: 'Failed processing' }
  if (file.processingStatus === MediaProcessingStatus.Processing)
    return { kind: 'processing', label: 'Processing' }
  if (file.isUploaded && file.virusScanStatus === VirusScanStatus.Pending)
    return { kind: 'pending-scan', label: 'Pending scan' }
  return { kind: 'ok', label: '' }
}
